import React, { useMemo, useState } from "react";
import {
  grondstofAfboekingen,
  halffabAfboekingen,
  hulpstofAfboekingen,
} from "@/lib/voorraadHistory";

const normalize = (value) => String(value || "").trim().toLowerCase();

export default function AfboekControle({
  rawMaterials,
  helpers,
  entries,
  halffabRuns,
  afkeur,
  expeditie,
  hulpstofInkoop,
  onOpenRecall,
}) {
  const [query, setQuery] = useState("");
  const [categorie, setCategorie] = useState("Alle");

  const rows = useMemo(() => {
    const rawRows = (rawMaterials || []).flatMap((item) => (
      grondstofAfboekingen(item.naam, entries, halffabRuns, afkeur, expeditie).map((row) => ({
        ...row,
        categorie: "Grondstof",
        artikelnummer: item.artikelnummer || "",
        naam: item.naam,
      }))
    ));
    const hulpRows = (helpers || []).flatMap((item) => (
      hulpstofAfboekingen(item.naam, entries, halffabRuns, afkeur, hulpstofInkoop).map((row) => ({
        ...row,
        categorie: "Hulpstof",
        artikelnummer: item.artikelnummer || item.artikelnr || "",
        naam: item.naam,
      }))
    ));
    const names = [...new Set((halffabRuns || []).map((run) => run.recept).filter(Boolean))];
    const halfRows = names.flatMap((naam) => (
      halffabAfboekingen(naam, entries, halffabRuns).map((row) => ({
        ...row,
        categorie: "Halffabricaat",
        artikelnummer: "",
        naam,
      }))
    ));
    return [...rawRows, ...hulpRows, ...halfRows].sort((a, b) => (
      String(b.datum || "").localeCompare(String(a.datum || ""))
    ));
  }, [rawMaterials, helpers, entries, halffabRuns, afkeur, expeditie, hulpstofInkoop]);

  const filteredRows = useMemo(() => {
    const term = normalize(query);
    return rows.filter((row) => {
      if (categorie !== "Alle" && row.categorie !== categorie) return false;
      if (!term) return false;
      return [
        row.artikelnummer,
        row.naam,
        row.tracecode,
        row.bron,
        row.detail,
      ].some((value) => normalize(value).includes(term));
    });
  }, [rows, query, categorie]);

  return (
    <section
      className="mb-5 rounded-xl border border-blue-200 bg-blue-50/60 p-4"
      data-testid="afboek-controle-card"
    >
      <div className="mb-3">
        <h2 className="text-lg font-bold text-slate-900">Afboek-controle</h2>
        <p className="text-sm text-slate-600">
          Zoek op artikelnaam, artikelnummer of tracecode. Hier staan grondstoffen,
          hulpstoffen en halffabricaten samen; open daarna dezelfde tracecode in Recall.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[240px] flex-1">
          <span className="mb-1 block text-xs font-semibold text-slate-600">Zoeken</span>
          <input
            data-testid="afboek-controle-input"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Artikelnaam, artikelnummer of tracecode"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-semibold text-slate-600">Categorie</span>
          <select
            data-testid="afboek-controle-categorie"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            value={categorie}
            onChange={(event) => setCategorie(event.target.value)}
          >
            <option value="Alle">Alle afboekingen</option>
            <option value="Grondstof">Grondstoffen</option>
            <option value="Hulpstof">Hulpstoffen</option>
            <option value="Halffabricaat">Halffabricaten</option>
          </select>
        </label>
      </div>
      {query.trim() && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="afboek-controle-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Categorie</th>
                <th className="px-3 py-2">Artikelnr.</th>
                <th className="px-3 py-2">Artikel</th>
                <th className="px-3 py-2">Afgeboekt in</th>
                <th className="px-3 py-2">Tracecode / lot</th>
                <th className="px-3 py-2 text-right">Kg</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr data-testid="afboek-controle-empty">
                  <td className="px-3 py-6 text-center text-slate-400" colSpan={8}>
                    Geen afboekingen gevonden voor deze zoekopdracht.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr
                    key={`${row.categorie}-${row.naam}-${row.datum}-${index}`}
                    data-testid="afboek-controle-row"
                    className="border-t border-slate-100"
                  >
                    <td className="whitespace-nowrap px-3 py-2">{row.datum || "—"}</td>
                    <td className="px-3 py-2">{row.categorie}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.artikelnummer || "—"}</td>
                    <td className="px-3 py-2 font-medium">{row.naam}</td>
                    <td className="px-3 py-2">{row.bron || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.tracecode || "niet vastgelegd"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {Number(row.kg || 0).toLocaleString("nl-NL", {
                        maximumFractionDigits: 1,
                      })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid="afboek-controle-recall"
                        disabled={!row.tracecode}
                        onClick={() => onOpenRecall?.({ code: row.tracecode })}
                        className="rounded-md border border-[#BE1E2D] px-2 py-1 text-xs font-semibold
                          text-[#BE1E2D] transition-colors hover:bg-rose-50 disabled:cursor-not-allowed
                          disabled:border-slate-200 disabled:text-slate-400"
                        title={row.tracecode ? "Open deze tracecode in Recall" : "Geen tracecode vastgelegd"}
                      >
                        Recall controleren
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}